import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { fetchProfile } from "@/lib/api";

export default function About() {
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    fetchProfile().then(setProfile).catch(() => {});
  }, []);

  if (!profile) {
    return (
      <main className="min-h-screen bg-zinc-950 flex items-center justify-center text-zinc-500 text-sm uppercase tracking-[0.3em]">
        Loading…
      </main>
    );
  }

  return (
    <main data-testid="about-page" className="bg-zinc-950 text-zinc-50 pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid grid-cols-12 gap-x-12 gap-y-16">
        <div className="col-span-12 md:col-span-7">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-6">
            Profile · {profile.roles.join(" / ")}
          </p>
          <h1
            data-testid="about-headline"
            className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-tight"
          >
            One filmmaker.
            <br />
            <span className="italic text-zinc-400">Every role.</span>
          </h1>

          <div className="mt-16 max-w-2xl space-y-6 text-lg leading-relaxed text-zinc-300">
            <p>{profile.bio}</p>
            <p className="text-zinc-400">
              Today, Qissa Studios collaborates with brands and platforms across India and
              abroad — Netflix, Google, RedBull, SanDisk, Apollo, IndiaFirst Life — while
              continuing to develop personal short films and poetry. Each craft sharpens
              the others.
            </p>
          </div>
        </div>

        <aside className="col-span-12 md:col-span-4 md:col-start-9">
          <div className="aspect-[3/4] overflow-hidden">
            <img
              src={profile.portrait}
              alt={profile.name}
              className="w-full h-full object-cover"
            />
          </div>
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mt-6">
            {profile.name}
          </p>
        </aside>
      </section>

      {/* Awards */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-32 md:py-40 grid grid-cols-12 gap-x-12 gap-y-10">
        <div className="col-span-12 md:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Selected recognition
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <ul className="divide-y divide-zinc-900">
            {profile.awards.map((a) => (
              <li
                key={`${a.year}-${a.title}`}
                data-testid={`about-award-${a.year}`}
                className="grid grid-cols-12 gap-4 py-6 items-baseline"
              >
                <span className="col-span-2 text-zinc-500 text-sm tabular-nums">
                  {a.year}
                </span>
                <span className="col-span-7 font-serif text-2xl md:text-3xl text-zinc-100">
                  {a.title}
                </span>
                <span className="col-span-3 text-[11px] uppercase tracking-[0.25em] text-zinc-500 text-right">
                  {a.org}
                </span>
                <span className="col-span-12 md:col-start-3 md:col-span-9 text-zinc-500 text-sm italic">
                  for <span className="text-zinc-300 not-italic">{a.for}</span>
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Press */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 pb-32 md:pb-40 grid grid-cols-12 gap-x-12 gap-y-10">
        <div className="col-span-12 md:col-span-4">
          <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500">
            Press
          </p>
        </div>
        <div className="col-span-12 md:col-span-8">
          <ul className="divide-y divide-zinc-900">
            {profile.press.map((p) => (
              <li key={p.title}>
                <a
                  href={p.url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`about-press-${p.outlet.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group grid grid-cols-12 gap-4 py-6 items-center hover:bg-zinc-900/30 transition-colors px-2"
                >
                  <span className="col-span-3 text-[11px] uppercase tracking-[0.25em] text-zinc-400">
                    {p.outlet}
                  </span>
                  <span className="col-span-8 font-serif text-xl md:text-2xl text-zinc-100 group-hover:italic transition-all">
                    {p.title}
                  </span>
                  <span className="col-span-1 justify-self-end text-zinc-500 group-hover:text-white">
                    <ArrowUpRight className="h-5 w-5" strokeWidth={1.5} />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-900 px-6 md:px-12 lg:px-16 py-24 md:py-32 max-w-[1600px] mx-auto">
        <p className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-8">
          Currently
        </p>
        <h2 className="font-serif text-4xl md:text-6xl leading-tight max-w-3xl">
          Open to features, editorial commissions,
          <span className="italic text-zinc-400"> and the right collaborators.</span>
        </h2>
        <Link
          to="/contact"
          data-testid="about-cta-contact"
          className="mt-12 group inline-flex items-center gap-3 bg-white text-zinc-950 px-8 py-4 text-[11px] uppercase tracking-[0.3em] hover:bg-zinc-200 transition-colors"
        >
          Get in touch
          <ArrowUpRight className="h-4 w-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" strokeWidth={1.5} />
        </Link>
      </section>
    </main>
  );
}
